import { useEffect, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const SLIDES = [
  {
    src: "https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?auto=format&fit=crop&w=1400&q=80",
    tag: "Excavator",
    title: "Site clearance in progress",
    caption: "Heavy machinery clearing debris and preparing the site for build start.",
  },
  {
    src: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1400&q=80",
    tag: "Dump truck",
    title: "Loaded and hauled away",
    caption: "Mixed construction waste hauled to licensed disposal facilities.",
  },
  {
    src: "https://images.unsplash.com/photo-1590496793929-36417d3117de?auto=format&fit=crop&w=1400&q=80",
    tag: "Skip bin",
    title: "On-site skip drops",
    caption: "Skip bin coordination for builder waste, offcuts and packaging.",
  },
  {
    src: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1400&q=80",
    tag: "Vacant lot",
    title: "Vacant lot clean-up",
    caption: "Overgrowth, dumped rubbish and loose debris cleared before construction.",
  },
  {
    src: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1400&q=80",
    tag: "Construction",
    title: "Builder waste removal",
    caption: "Timber offcuts, plasterboard, bricks and general site rubbish.",
  },
  {
    src: "https://images.unsplash.com/photo-1521790361543-f645cf042ec4?auto=format&fit=crop&w=1400&q=80",
    tag: "End-of-build",
    title: "Handover-ready sites",
    caption: "Final site tidy before inspection, photography or settlement.",
  },
];

export default function CleanupCarousel() {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start", dragFree: true });
  const [selected, setSelected] = useState(0);

  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);
  const scrollTo = useCallback((i) => embla && embla.scrollTo(i), [embla]);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelected(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();

    // Auto-play
    const id = setInterval(() => {
      if (embla) embla.scrollNext();
    }, 4500);
    return () => {
      clearInterval(id);
      embla.off("select", onSelect);
    };
  }, [embla]);

  return (
    <section className="py-24 bg-white border-t border-border" data-testid="cleanup-carousel">
      <div className="rs-container">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="rs-eyebrow">On the ground</span>
            <h2 className="mt-3 text-4xl md:text-5xl uppercase">Excavators, dump trucks & skip bins in action.</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">Real site clean-up work — from vacant lots and builder waste to handover-ready tidy-ups. The right machinery, matched to the job.</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="carousel-prev" onClick={scrollPrev} aria-label="Previous slide" className="w-11 h-11 grid place-items-center border-2 border-asphalt hover:bg-asphalt hover:text-white transition-colors">
              <ChevronLeft size={18} />
            </button>
            <button data-testid="carousel-next" onClick={scrollNext} aria-label="Next slide" className="w-11 h-11 grid place-items-center border-2 border-asphalt bg-asphalt text-white hover:bg-safety hover:border-safety transition-colors">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex touch-pan-y pl-4 md:pl-[calc((100vw-1200px)/2+16px)] pr-4">
          {SLIDES.map((s, i) => (
            <div key={s.title} className="shrink-0 basis-[85%] sm:basis-[60%] md:basis-[45%] lg:basis-[36%] pr-4" data-testid={`carousel-slide-${i}`}>
              <div className="relative overflow-hidden border border-border group h-[420px]">
                <img
                  src={s.src}
                  alt={s.title}
                  loading={i === 0 ? "eager" : "lazy"}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-asphalt/95 via-asphalt/25 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-between">
                  <span className="self-start bg-safety text-white text-[11px] font-bold uppercase tracking-[0.2em] px-2.5 py-1">{s.tag}</span>
                  <div>
                    <h3 className="font-display text-white text-2xl md:text-3xl uppercase leading-tight">{s.title}</h3>
                    <p className="text-white/85 text-sm mt-2 max-w-sm">{s.caption}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rs-container mt-8 flex items-center gap-2">
        {SLIDES.map((s, i) => (
          <button
            key={s.title}
            onClick={() => scrollTo(i)}
            data-testid={`carousel-dot-${i}`}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1 transition-all ${selected === i ? "w-10 bg-safety" : "w-6 bg-border hover:bg-asphalt"}`}
          />
        ))}
      </div>
    </section>
  );
}
